import moment from "moment";
import chartsConfig, { ChartConfig, ChartInputData } from "../constants/charts";
import { metricColor, rgbToRgba } from ".";
import { Theme } from "@material-ui/core";
import { refine } from "./nullHelpers";
import { DateRange } from "../api/agritech";

export const getChartConfig = (
  data: ChartInputData[] | undefined,
  config: {
    chartType: keyof typeof chartsConfig;
    theme: Theme;
    metrics: {
      ref: string;
      color: string | [string, string];
      short_name: string;
    }[];
    field?: any;
    range?: DateRange;
    metricLabels?: Record<string, string>;
  }
) => {
  const { chartType, theme, metrics, field } = config;
  const chartConfig = chartsConfig[chartType];
  return {
    datasets: refine(
      Object.entries(chartConfig.metrics),
      ([ref, chartMetricConfig]: [string, ChartConfig["metrics"][string]]) => {
        const type = chartMetricConfig ? chartMetricConfig.type : null;
        const hidden = chartMetricConfig ? chartMetricConfig.hidden : null;
        const metric = metrics.filter((item) => item.ref === ref);
        const metricsSettings = metric.length ? metric[0] : null;
        let minValue = Infinity;
        const color = metricsSettings
          ? metricColor(theme, metricsSettings.color)
          : theme.palette.grey[500];

        let dataset = [];
        let hasValue = false;
        let persistValue: number | undefined;
        if (typeof chartMetricConfig.aggregation == "function") {
          // This calculates values as a function of time, using a custom aggregation function
          dataset = chartMetricConfig.aggregation(
            data?.sort((a, b) => {
              return moment(a.timestamp).diff(b.timestamp);
            }) ?? []
          );
          hasValue = true;
        } else {
          // Processes each row of data and finds the value for the current metric

          dataset = refine(data ?? [], (record) => {
            let y: number | undefined;
            if (chartMetricConfig.formatter) {
              // Uses custom formatter function to get the value if set (e.g. if y = sum of other metrics)
              y = chartMetricConfig.formatter(record, field);
            } else {
              // Ensures y is a number value
              const sy = record[chartMetricConfig.property ?? ref];
              y = typeof sy === "string" ? parseFloat(sy) : sy;
            }
            if (y === undefined && !chartMetricConfig.includeValues) return;
            hasValue = true;
            if (chartMetricConfig.persist) {
              persistValue = y;
            }
            if (y < minValue) {
              minValue = y;
            }
            return {
              x: record.timestamp,
              y,
              values: chartMetricConfig.includeValues ? record : undefined,
              label: ref === "squeeze" ? record.label : undefined,
            };
          });
          if (persistValue !== undefined) {
            // add an extra point at the end with the last known value, to persist the line to the end of the chart
            dataset.push({
              x: moment(config.range?.end_date).valueOf(),
              y: persistValue,
              values: undefined,
              label: undefined,
            });
          }
        }

        if (!hasValue) {
          return undefined;
        }

        let pointRadius = 0;
        if (ref === "squeeze") {
          pointRadius = 5;
        } else if (chartConfig.group === "agronomics") {
          pointRadius = 1.5;
        }

        return {
          xAxisID: "x",
          yAxisID: chartMetricConfig.axisId,
          label: config.metricLabels?.[ref] || metricsSettings?.short_name || "Unknown",
          type: type === "area" ? "line" : type ?? "line",
          hidden: hidden ?? false,
          data: dataset,
          fill: type === "area",
          borderWidth: 2,
          borderColor: color,
          borderDash: chartMetricConfig.style === "dashed" ? [6, 4] : undefined,
          stepped: chartMetricConfig.persist ?? false,
          property: chartMetricConfig.property ?? ref,
          backgroundColor:
            type === "area" && color !== undefined
              ? rgbToRgba(color, 0.4)
              : color,
          pointRadius,
          pointBorderColor: color,
          pointBackgroundColor: color,
          datalabels: {
            display: type === "scatter",
          },
          parsing: false,
          barPercentage: 0.8,
          barThickness: "flex",
          maxBarThickness: 20,
          spanGaps: true,
          helpers: { minValue },
        };
      }
    ),
  };
};
